import { useForm, useStore } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";
import { FormGroup } from "../../../components/FormGroup";
import { FormLabel } from "../../../components/FormLabel";
import { Form } from "../../../components/Form";
import { Input } from "../../../components/Input";
import { FormSection } from "../../../components/FormSection";
import { OfferType } from "@KassaroAB/shared/types/enums/OfferType";
import type { OfferBuyXPayY } from "@KassaroAB/shared/types/offer";
import { OfferCommonFields } from "./OfferCommonFields";
import { OfferTargetFields } from "./OfferTargetFields";
import type { OfferTypeFormProps } from "./offerFormTypes";
import { Fields } from "./offerFormStyles";
import {
  buildOfferBase,
  emptyOfferTarget,
  getOfferCommonDefaults,
} from "./offerUtils";
import { useOfferFormCatalog } from "./useOfferFormCatalog";
import { useOfferFormLifecycle } from "./useOfferFormLifecycle";

export const BuyXPayYForm = ({
  formId,
  offer,
  savedTick,
  onSubmit,
  onDirtyChange,
  onTypeChange,
}: OfferTypeFormProps) => {
  const { t } = useTranslation();
  const { productOptions, groupOptions } = useOfferFormCatalog();
  const buyXPayYOffer =
    offer?.type === OfferType.BUY_X_PAY_Y ? (offer as OfferBuyXPayY) : null;
  const isEditing = !!offer;

  const form = useForm({
    defaultValues: {
      ...getOfferCommonDefaults(
        buyXPayYOffer ??
          (offer ? { ...offer, type: OfferType.BUY_X_PAY_Y } : undefined),
      ),
      type: OfferType.BUY_X_PAY_Y as OfferType,
      target:
        buyXPayYOffer?.target ??
        (offer && "target" in offer ? offer.target : emptyOfferTarget()),
      buyQuantity: buyXPayYOffer?.buyQuantity ?? 2,
      payQuantity: buyXPayYOffer?.payQuantity ?? 1,
    },
    onSubmit: ({ value }) => {
      onSubmit({
        ...buildOfferBase(value),
        type: OfferType.BUY_X_PAY_Y,
        target: value.target,
        buyQuantity: Math.max(1, Number(value.buyQuantity)),
        payQuantity: Math.min(
          Math.max(1, Number(value.payQuantity)),
          Math.max(1, Number(value.buyQuantity)),
        ),
      });
    },
  });

  const isDirty = useStore(form.store, (state) => !state.isDefaultValue);
  const buyQuantity = useStore(form.store, (state) => state.values.buyQuantity);
  useOfferFormLifecycle({ form, savedTick, onDirtyChange, isDirty });

  return (
    <Form
      id={formId}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <OfferCommonFields
        form={form}
        isEditing={isEditing}
        onTypeChange={onTypeChange}
      />
      <FormSection title={t("offerForm.additionalSettings")}>
        <Fields>
          <form.Field name="target">
            {(field) => (
              <OfferTargetFields
                value={field.state.value}
                onChange={field.handleChange}
                products={productOptions}
                groups={groupOptions}
                productsLabel={t("offerForm.products")}
                groupsLabel={t("offerForm.productGroups")}
              />
            )}
          </form.Field>
          <form.Field name="buyQuantity">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("offerForm.buyQuantity")}</FormLabel>
                <Input
                  type="number"
                  min={1}
                  max={99}
                  value={field.state.value}
                  onChange={(e) => {
                    const next = Math.max(1, Number(e.target.value) || 1);
                    field.handleChange(next);
                    form.setFieldValue("payQuantity", (pay) =>
                      Math.min(pay, next),
                    );
                  }}
                  onBlur={field.handleBlur}
                  required
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="payQuantity">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("offerForm.payQuantity")}</FormLabel>
                <Input
                  type="number"
                  min={1}
                  max={buyQuantity}
                  value={field.state.value}
                  onChange={(e) =>
                    field.handleChange(
                      Math.min(
                        buyQuantity,
                        Math.max(1, Number(e.target.value) || 1),
                      ),
                    )
                  }
                  onBlur={field.handleBlur}
                  required
                />
              </FormGroup>
            )}
          </form.Field>
        </Fields>
      </FormSection>
    </Form>
  );
};
